import axios from "axios";
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "@/App";
import "./i18n";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "@/pages/fonts.css";
import "@/theme.css";
import { getAccessToken, triggerRefresh, clearAccessToken } from "./services/tokenService";

const getPathname = (url) => {
  if (typeof url !== "string") return "";
  try {
    // If it's a full URL
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("//")) {
      return new URL(url).pathname;
    }
    // If it's a relative URL, it might contain query params, so strip them
    return url.split("?")[0];
  } catch (e) {
    return url;
  }
};

const AUTH_ISSUER = (process.env.REACT_APP_AUTH_ISSUER || "/auth").replace(/\/+$/, "");
const isAuthUrl = (url) => {
  const pathname = getPathname(url);
  const cleanUrl = typeof url === "string" ? url : "";
  return (
    pathname === `${AUTH_ISSUER}/refresh` ||
    pathname === `${AUTH_ISSUER}/login` ||
    pathname === `${AUTH_ISSUER}/logout` ||
    cleanUrl.startsWith(`${AUTH_ISSUER}/refresh`) ||
    cleanUrl.startsWith(`${AUTH_ISSUER}/login`) ||
    cleanUrl.startsWith(`${AUTH_ISSUER}/logout`)
  );
};

const isBackendRequest = (url, pathname, backendUrl) => {
  if (!backendUrl) return false;

  const isAbsolute =
    backendUrl.startsWith("http://") ||
    backendUrl.startsWith("https://") ||
    backendUrl.startsWith("//");

  if (isAbsolute) {
    try {
      const parsedRequest = new URL(url.startsWith("//") ? "https:" + url : url, window.location.origin);
      const parsedBackend = new URL(backendUrl.startsWith("//") ? "https:" + backendUrl : backendUrl, window.location.origin);

      return (
        parsedRequest.origin === parsedBackend.origin &&
        parsedRequest.pathname.startsWith(parsedBackend.pathname)
      );
    } catch (e) {
      return false;
    }
  }

  return pathname.startsWith(backendUrl);
};

// Global Fetch Interceptor
const originalFetch = window.fetch;
if (originalFetch) {
  window.fetch = async (resource, config) => {
    let url =
      resource && typeof resource === "object" && "url" in resource
        ? resource.url
        : resource;
    const pathname = getPathname(url);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const AE_BACKEND_URL = process.env.REACT_APP_AE_BACKEND_URL;
    const isBackend =
      isBackendRequest(url, pathname, BACKEND_URL) ||
      isBackendRequest(url, pathname, AE_BACKEND_URL) ||
      pathname.startsWith("/backend/") ||
      pathname.startsWith("/ae-backend/");

    const isAuthEndpoint = isAuthUrl(url);

    if (isBackend && !isAuthEndpoint) {
      try {
        const token = getAccessToken();
        if (token) {
          if (resource && typeof resource === "object" && "url" in resource) {
            const headers = new Headers(resource.headers || {});
            if (!headers.has("Authorization")) {
              headers.set("Authorization", `Bearer ${token}`);
            }
            resource = new Request(resource, { headers });
          } else {
            config = config || {};
            const headers = config.headers ? { ...config.headers } : {};
            const hasAuth = Object.keys(headers).some(
              (k) => k.toLowerCase() === "authorization",
            );
            if (!hasAuth) {
              headers["Authorization"] = `Bearer ${token}`;
            }
            config.headers = headers;
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Fetch auth inject failed:", err);
      }
    }

    const isChatRequest = url && typeof url === "string" && url.includes("/api/v1/answer-engine/chat-request");
    let requestPayload = {};
    const questionTimestamp = isChatRequest ? new Date().toISOString() : null;

    if (isChatRequest) {
      try {
        const isRequestObject = resource instanceof Request;
        if (isRequestObject) {
          const clonedReq = resource.clone();
          requestPayload = await clonedReq.json();
        } else if (config && config.body) {
          if (typeof config.body === "string") {
            requestPayload = JSON.parse(config.body);
          } else if (config.body instanceof Blob) {
            const text = await config.body.text();
            requestPayload = JSON.parse(text);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error parsing chat request body in global interceptor:", e);
      }
    }

    let response = await originalFetch(resource, config);

    // Reactive 401 retry queueing
    if (response && response.status === 401 && isBackend && !isAuthEndpoint) {
      console.error(`[${new Date().toISOString()}] Received 401 response, attempting token refresh.`);

      const alreadyRetried = config && config._retry;
      if (!alreadyRetried) {
        config = config || {};
        config._retry = true;

        try {
          // Re-acquire fresh token using the coalesced refresh call
          const newToken = await triggerRefresh();
          
          if (resource && typeof resource === "object" && "url" in resource) {
            const newHeaders = new Headers(resource.headers || {});
            newHeaders.set("Authorization", `Bearer ${newToken}`);
            resource = new Request(resource, { headers: newHeaders });
          } else {
            config.headers = config.headers || {};
            config.headers["Authorization"] = `Bearer ${newToken}`;
          }
          
          response = await originalFetch(resource, config);
        } catch (refreshErr) {
          clearAccessToken();
          window.dispatchEvent(new CustomEvent("auth:logout-redirect"));
        }
      }
    }

    if (isChatRequest && response) {
      const answerTimestamp = new Date().toISOString();
      let responseData = null;

      try {
        const clonedResponse = response.clone();
        responseData = await clonedResponse.json();

        if (!response.ok) {
          if (responseData && typeof responseData === "object") {
            responseData.success = false;
          } else {
            responseData = {
              success: false,
              error: typeof responseData === "string" ? responseData : `HTTP ${response.status}: ${response.statusText}`,
              status: response.status,
              statusText: response.statusText,
            };
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error parsing chat response in global interceptor:", e);

        let errorText = "";
        try {
          const clonedResponseText = response.clone();
          errorText = await clonedResponseText.text();
        } catch (textErr) {
          // Ignore error reading response text
        }

        responseData = {
          success: false,
          error: errorText || e.message || `HTTP ${response.status}: ${response.statusText}`,
          status: response?.status,
          statusText: response?.statusText,
        };
      }

      if (responseData) {
        window.dispatchEvent(
          new CustomEvent("ae-widget:chat-request-completed", {
            detail: {
              requestPayload,
              responseData,
              questionTimestamp,
              answerTimestamp,
            },
          })
        );
      }
    }

    return response;
  };
}

// Global Axios Interceptor
if (axios && axios.interceptors) {
  axios.interceptors.request.use(
    (config) => {
      try {
        const token = getAccessToken();
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
        const AE_BACKEND_URL = process.env.REACT_APP_AE_BACKEND_URL || "";
        const url = config.url || "";
        const pathname = getPathname(url);
        const isBackend =
          isBackendRequest(url, pathname, BACKEND_URL) ||
          isBackendRequest(url, pathname, AE_BACKEND_URL) ||
          pathname.startsWith("/backend/") ||
          pathname.startsWith("/ae-backend/");

        const isAuthEndpoint = isAuthUrl(url);

        if (token && isBackend && !isAuthEndpoint) {
          const headers = config.headers || {};
          const hasAuth = Object.keys(headers).some(
            (k) => k.toLowerCase() === "authorization",
          );
          if (!hasAuth) {
            headers["Authorization"] = `Bearer ${token}`;
          }
          config.headers = headers;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Axios auth inject failed:", err);
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  let isRefreshingAxios = false;
  let failedQueue = [];

  const processQueue = (err, token = null) => {
    failedQueue.forEach((prom) => {
      if (err) prom.reject(err);
      else prom.resolve(token);
    });
    failedQueue = [];
  };

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const url = originalRequest?.url || "";
      const isAuthEndpoint = isAuthUrl(url);

      if (
        error.response &&
        error.response.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint
      ) {
        console.error(`[${new Date().toISOString()}] Received 401 response, attempting token refresh.`);

        if (isRefreshingAxios) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers = originalRequest.headers || {};
              if (typeof originalRequest.headers.set === "function") {
                originalRequest.headers.set("Authorization", `Bearer ${token}`);
              } else {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
              }
              return axios(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshingAxios = true;

        try {
          const newToken = await triggerRefresh();
          processQueue(null, newToken);
          originalRequest.headers = originalRequest.headers || {};
          if (typeof originalRequest.headers.set === "function") {
            originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
          } else {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          }
          return axios(originalRequest);
        } catch (refreshErr) {
          processQueue(refreshErr, null);
          clearAccessToken();
          window.dispatchEvent(new CustomEvent("auth:logout-redirect"));
          return Promise.reject(refreshErr);
        } finally {
          isRefreshingAxios = false;
        }
      }
      return Promise.reject(error);
    },
  );
}

async function prepareAndRender() {
  if (process.env.NODE_ENV === "development" && process.env.REACT_APP_USE_MOCK_BACKEND === "true") {
    // Start MSW for local environment-only mocking — await the worker.start() promise
    // so the service worker is fully registered before any requests are made.
    try {
      // dynamic import ensures bundlers don't include MSW in production builds
      const { default: mswReady } = await import("./mocks/initMocks");
      await mswReady;
    } catch (err) {
      // ignore if import fails (e.g., msw not installed yet)
      // eslint-disable-next-line no-console
      console.warn("Could not start MSW (local mocks):", err);
    }
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Data is considered fresh for 5 minutes — no background refetch within this window.
        staleTime: 5 * 60 * 1000, // 5 minutes
        // Inactive cache entries are kept in memory for 10 minutes after their
        // last subscriber unmounts. This is intentionally 2× staleTime so that
        // navigating away and back within 10 minutes still serves cached data
        // (shown instantly while a background refetch runs), rather than
        // showing a loading spinner on every page revisit.
        gcTime: 10 * 60 * 1000, // 10 minutes
        // Silently refetch stale data in the background when the user returns
        // to the tab. Because cached data is shown immediately, there is no
        // loading spinner — the UI just quietly updates once fresh data arrives.
        refetchOnWindowFocus: true,
        // Silently refetch after a network outage is recovered.
        refetchOnReconnect: true,
        // When a component mounts and the cached data is still fresh (within
        // staleTime), skip the network request entirely. When stale, refetch
        // in the background while showing the cached data immediately.
        refetchOnMount: true,
        retry: 1,
      },
    },
  });

  const container = document.getElementById("root");
  if (container) {
    const root = ReactDOM.createRoot(container);
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </React.StrictMode>,
    );
  }
}

if (process.env.NODE_ENV !== "test") {
  prepareAndRender();
}
